import React, {useEffect, useState} from 'react';
import {canInstall, installStaffApp, isStaffOrigin, registerStaffPwa, pushRequest, disableStaffPush} from '@/lib/staffPwa';

const notificationTypes = [
  {kind: 'booking', label: 'New bookings', description: 'New appointments added to the calendar.'},
  {kind: 'cancellation', label: 'Cancellations', description: 'Confirmed appointment cancellations.'},
  {kind: 'form', label: 'Completed forms', description: 'Customers finish their forms.'},
];
const defaultPreferences = {booking: true, cancellation: true, form: true};

export function StaffApp() {
  const [installable, setInstallable] = useState(canInstall());
  const [enabled, setEnabled] = useState(false);
  const [settings, setSettings] = useState(null);
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const standalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;
  const apple = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  useEffect(() => {
    const update = () => setInstallable(canInstall());
    window.addEventListener('pout-install-ready', update);
    if (isStaffOrigin()) pushRequest('config').then(setSettings).catch(error => setMessage(error.message));
    if (isStaffOrigin() && supported) registerStaffPwa().then(async registration => {
      const sub = await registration.pushManager.getSubscription();
      if (sub) {
        const status = await pushRequest('status', {endpoint: sub.endpoint});
        setEnabled(status.enabled); setPreferences(status.preferences || defaultPreferences);
      }
    }).catch(error => setMessage(error.message));
    return () => window.removeEventListener('pout-install-ready', update);
  }, [supported]);
  const action = async (fn) => {
    setBusy(true); setMessage('');
    try { await fn(); } catch (error) { setMessage(error.message || 'Please try again.'); } finally { setBusy(false); }
  };
  const enable = () => action(async () => {
    // Permission must be requested directly from the user's button press on iOS.
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') throw new Error('Notifications are blocked. Allow notifications for Pout Staff in your device or browser settings, then try again.');
    const registration = await registerStaffPwa();
    if (!registration.active) await navigator.serviceWorker.ready;
    const key = settings.public_key.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(key + '='.repeat((4 - key.length % 4) % 4)), c => c.charCodeAt(0));
    const subscription = await registration.pushManager.getSubscription() || await registration.pushManager.subscribe({userVisibleOnly: true, applicationServerKey: bytes});
    const result = await pushRequest('subscribe', subscription.toJSON());
    setPreferences(result.preferences || defaultPreferences);
    setEnabled(true); setMessage('Notifications enabled on this device. Use Send test notification to check delivery.');
  });
  const changePreference = (type, checked) => action(async () => {
    const sub = await (await registerStaffPwa()).pushManager.getSubscription();
    if (!sub) throw new Error('Enable notifications again on this device.');
    const result = await pushRequest('preferences', {endpoint: sub.endpoint, kind: type.kind, enabled: checked});
    setPreferences(result.preferences);
    setMessage(`${type.label} alerts turned ${result.preferences[type.kind] ? 'on' : 'off'} on this device.`);
  });
  const button = 'rounded-full px-5 py-3 bg-[#c3a052] text-[#101A3A] font-semibold disabled:opacity-50';
  return <div className="p-5 md:p-8 max-w-3xl space-y-6">
    <h1 className="text-3xl font-serif">App &amp; notifications</h1>
    {!isStaffOrigin() ? <p>Open <a className="underline" href="https://staff.poutaesthetics.co.uk/staff/settings/app">the permanent staff portal</a> to install the app and enable notifications.</p> : <>
      <section className="surface-highlight rounded-2xl p-6 space-y-4">
        <h2 className="text-2xl font-serif text-[#c3a052]">Pout Staff on your home screen</h2>
        {standalone ? <p>You’re using the installed app.</p> : <>
          {installable && <button className={button} disabled={busy} onClick={() => action(async () => {await installStaffApp(); setInstallable(canInstall());})}>Install Pout Staff</button>}
          <p>{apple ? 'In Safari, tap Share, then Add to Home Screen. Open Pout Staff from its new icon before enabling notifications.' : 'Use Install Pout Staff above, or open your browser menu and choose Install app or Add to Home screen.'}</p>
        </>}
      </section>
      <section className="rounded-2xl border border-[#c3a052] bg-white p-6 space-y-4">
        <h2 className="text-2xl font-serif">Staff notifications</h2>
        <p>Receive new bookings, cancellations and completed forms. Customer details stay inside the signed-in portal.</p>
        <p>Enable this on each device you want to receive alerts. Signing out turns off alerts on that device.</p>
        {!supported || (apple && !standalone) ? <p>Install and open the app first. Notifications need a supported browser and, on iPhone or iPad, iOS/iPadOS 16.4 or later.</p> : !settings ? <p>Checking notification settings…</p> : !settings.enabled ? <p>Notification setup is not complete on the server yet.</p> : <>
          <p>{enabled ? 'Enabled on this device' : 'Not enabled on this device'}</p>
          {enabled && <fieldset disabled={busy} className="space-y-4">
            <legend className="font-semibold mb-3">Choose your alerts</legend>
            {notificationTypes.map(type => <div key={type.kind} className="flex items-center justify-between gap-4 border-b border-navy/10 pb-4">
              <div className="min-w-0"><label htmlFor={`alert-${type.kind}`} className="font-semibold cursor-pointer">{type.label}</label><p className="text-sm text-navy/70 mt-1">{type.description}</p></div>
              <button id={`alert-${type.kind}`} type="button" role="switch" aria-label={`${type.label} notifications`} aria-checked={preferences[type.kind]}
                disabled={busy} onClick={() => changePreference(type, !preferences[type.kind])}
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border border-navy/30 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold disabled:opacity-50 ${preferences[type.kind] ? 'bg-gold' : 'bg-navy/20'}`}>
                <span aria-hidden="true" className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${preferences[type.kind] ? 'translate-x-6' : 'translate-x-1'}`}/>
              </button>
            </div>)}
            <p className="text-sm text-navy/70">Choices are saved for this device. Turning an alert back on starts with new activity.</p>
            <p className="text-sm text-navy/70">Customer emails and WhatsApp messages follow your Communications settings.</p>
          </fieldset>}
          <div className="flex flex-wrap gap-3">
            {enabled ? <>
              <button className={button} disabled={busy} onClick={() => action(async () => {
                const sub = await (await registerStaffPwa()).pushManager.getSubscription();
                if (!sub) throw new Error('Enable notifications again on this device.');
                const result = await pushRequest('test', {endpoint: sub.endpoint}); setMessage(result.message);
              })}>Send test notification</button>
              <button className="rounded-full border px-5 py-3" disabled={busy} onClick={() => action(async () => {await disableStaffPush(); setEnabled(false); setMessage('Notifications turned off on this device.');})}>Turn off notifications</button>
            </> : <button className={button} disabled={busy} onClick={enable}>Enable notifications</button>}
          </div>
        </>}
      </section>
    </>}
    {message && <p role="status" className="rounded-xl border p-4">{message}</p>}
  </div>;
}
